import React, { useState, useEffect } from "react";
import { useDialog } from "../../contexts/DialogContext";
import { Request } from "../../hooks/Request";
import { requestAuth } from "../../components/services/RequestService";
import * as dayjs from "dayjs";
import { ExpandedBitacora } from "./ExpandedBitacora";

const SUB_VALUE_FIELDS = [
  "codigoDeInterno",
  "proveedor",
  "producto",
  "descripciónGeneralDeLaCompra",
  "subasta",
  "pesoBruto",
  "incoterms",
  "seguro",

  "naviera",
  "agenteCarga",
  "puertoDeEmbarque(Pol)",
  "puertoDeDestino(Pod)",
  "cantidad",
  "cantidadContenedores",
  "tamanoContenedor",
  "siglaDeContenedor",
  "siglaContenedor",
  "n°Bl",
  "precinto",
  "numeroFactura",
  "etd",
  "eta",
  "diasTransitoMaritimo",

  "cantidadEnviadaKg",
  "airwaybill",
  "aeropuertoDeOrigen(Aol)",
  "aeropuertoDeDestino(Aod)",
  "transporteTerrestre",
  "fechaDevoluciónContenedores",
  "modalidadDeCarga",

  "agenteDespachante",
  "nroDim",
  "fechaDam",
  "fechaPagoDeTributos",
  "asignaciónDeCanal",
  "almacénDestino",
  "fechaDeRecepción",

  "a13ParteModalidadDeTransporte",
  "modalidadCargaTransporte",

  "total(Kg)Ritex",
];

const TERRESTRIAL_TRANSIT_FIELDS = [
  {
    label: "Transporte terrestre",
    name: "transporteTerrestre",
    emphasis: true,
  },
  { label: "POL", name: "puertoDeEmbarque(Pol)" },
  { label: "POD", name: "puertoDeDestino(Pod)" },
  {
    label: "Fecha devolución contenedores",
    name: "fechaDevoluciónContenedores",
    type: "date",
  },
  { label: "N° BL", name: "n°Bl" },
  { label: "ETD", name: "etd", type: "date" },
  { label: "ETA", name: "eta", type: "date" },
  { label: "Modalidad despacho", name: "modalidadDeCarga" },
];

const MULTIMODAL_TRANSIT_FIELDS = [
  {
    label: "Transporte terrestre",
    name: "transporteTerrestre",
    emphasis: true,
  },
  { label: "Naviera", name: "naviera" },
  { label: "POL", name: "puertoDeEmbarque(Pol)" },
  { label: "POD", name: "puertoDeDestino(Pod)" },
  { label: "Cantidad de contenedores", name: "cantidad" },
  {
    label: "Fecha devolución contenedores",
    name: "fechaDevoluciónContenedores",
    type: "date",
  },
  { label: "N° BL", name: "n°Bl" },
  { label: "ETD", name: "etd", type: "date" },
  { label: "ETA", name: "eta", type: "date" },
  { label: "Modalidad despacho", name: "modalidadDeCarga" },
];

const AERIAL_TRANSIT_FIELDS = [
  { label: "Aeropuerto origen", name: "aeropuertoDeOrigen(Aol)" },
  { label: "Aeropuerto destino", name: "aeropuertoDeDestino(Aod)" },
  { label: "Cantidad enviada (KG)", name: "total(Kg)Ritex" },
  { label: "Airwaybill", name: "airwaybill" },
  { label: "ETD", name: "etd", type: "date" },
  { label: "ETA", name: "eta", type: "date" },
];

const SECTIONS = {
  generales: {
    label: "Generales",
    marker: "G",
    helper: "Identificación, proveedor, producto y modalidad.",
    fields: [
      {
        label: "Número de importación",
        name: "codigoDeInterno",
        emphasis: true,
      },
      { label: "Proveedor", name: "proveedor" },
      { label: "Cliente interno", name: "companyName" },
      { label: "Fecha de solicitud", name: "creationDate", type: "date" },
      {
        label: "Producto",
        name: "descripciónGeneralDeLaCompra",
        fallback: "producto",
      },
      { label: "Subasta", name: "subasta" },
      { label: "Cantidad (KG)", name: "cantidadKg", fallback: "pesoBruto" },
      { label: "Incoterms", name: "incoterms" },
      { label: "Seguro", name: "seguro" },
      {
        label: "Modalidad de transporte",
        name: "a13ParteModalidadDeTransporte",
        emphasis: true,
      },
    ],
  },
  transito: {
    label: "Tránsito",
    marker: "T",
    helper: "Carga, puertos, contenedor, BL, ETD/ETA y tránsito marítimo.",
  },
  nacionalizacion: {
    label: "Nacionalización",
    marker: "N",
    helper: "Despachante, DIM, DAM, tributos, canal, almacén y recepción.",
    fields: [
      {
        label: "Agente despachante",
        name: "agenteDespachante",
        emphasis: true,
      },
      { label: "Nro de DIM", name: "nroDim" },
      { label: "Fecha DAM", name: "fechaDam", type: "date" },
      {
        label: "Fecha pago de tributos",
        name: "fechaPagoDeTributos",
        type: "date",
      },
      { label: "Asignación de canal", name: "asignaciónDeCanal" },
      { label: "Almacén destino", name: "almacénDestino" },
      {
        label: "Fecha de recepción",
        name: "fechaDeRecepción",
        type: "date",
        emphasis: true,
      },
      // { label: "Documentos originales", name: "documentosOriginales" },
      // { label: "Tránsito terrestre", name: "tránsitoTerrestre" },
    ],
  },
};

function getTransitFields(row) {
  const transport = (row?.a13ParteModalidadDeTransporte || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (transport.includes("AEREO")) {
    return AERIAL_TRANSIT_FIELDS;
  }

  if (transport.includes("MULTIMODAL")) {
    return MULTIMODAL_TRANSIT_FIELDS;
  }

  if (transport.includes("TERRESTRE")) {
    return TERRESTRIAL_TRANSIT_FIELDS;
  }

  // Nunca devolver undefined
  return TERRESTRIAL_TRANSIT_FIELDS;
}
function buildFieldNamesQuery() {
  return SUB_VALUE_FIELDS.map((field) => `fieldnames=${field}`).join("&");
}

function resolveProcedureId(value) {
  return value?.id ?? value?.procedureId ?? value?.ProcedureId ?? null;
}

function hasProcedureId(value) {
  return value !== null && value !== undefined && value !== "";
}

function valueOf(row, field) {
  const value = row?.[field.name];
  if (value !== null && value !== undefined && value !== "") return value;

  if (field.name === "cantidad") {
    const containerQuantity = [row?.cantidadContenedores, row?.tamanoContenedor]
      .filter(Boolean)
      .join(" ");

    if (containerQuantity) return containerQuantity;
  }

  return field.fallback ? row?.[field.fallback] : value;
}

function formatValue(row, field) {
  const value = valueOf(row, field);
  if (!value || value === "0001-01-01T00:00:00") return "-";
  if (field.type === "date") return dayjs(value).format("DD/MM/YYYY");
  return value;
}

function safeJsonParse(value, fallback = {}) {
  try {
    if (!value) return fallback;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return fallback;
  }
}

function getSubDataValue(item, camelName, pascalName, fallback = "") {
  const value = item?.[camelName] ?? item?.[pascalName];
  return value ?? fallback;
}

function getContainerGroups(row) {
  const subDatas = row?.cantidadSubDatas;
  if (!Array.isArray(subDatas)) return [];

  return subDatas.map((item) => {
    const parsed = safeJsonParse(
      getSubDataValue(item, "description2", "Description2"),
    );

    return {
      quantity:
        parsed.quantity ?? getSubDataValue(item, "numberValue", "NumberValue"),
      containerType:
        parsed.containerType ?? getSubDataValue(item, "stringValue", "StringValue"),
      quantityLabel:
        parsed.quantityLabel || getSubDataValue(item, "label", "Label"),
      billOfLading:
        parsed.billOfLading || getSubDataValue(item, "description", "Description"),
      containers: Array.isArray(parsed.containers) ? parsed.containers : [],
    };
  });
}

function mergeWithFallback(base, override) {
  const merged = { ...base, ...override };
  Object.keys(base).forEach((key) => {
    if (key === "id") return;
    if (
      (merged[key] === null ||
        merged[key] === undefined ||
        merged[key] === "") &&
      base[key]
    ) {
      merged[key] = base[key];
    }
  });
  return merged;
}

export const ImportTrackingModal = ({ procedure }) => {
  const { dialogClose } = useDialog();
  const safeProcedure = procedure || {};
  const procedureId = resolveProcedureId(safeProcedure);

  const [activeTracking, setActiveTracking] = useState(null);
  const [expandedBitacoraId, setExpandedBitacoraId] = useState(null);
  const [parentBitacora, setParentBitacora] = useState([]);
  const [parentBitacoraLoaded, setParentBitacoraLoaded] = useState(false);
  const [parentValues, setParentValues] = useState({});
  const [parentValuesLoaded, setParentValuesLoaded] = useState(false);
  const [subValuesMap, setSubValuesMap] = useState({});

  const { data: subProcedures, loading: subProceduresLoaded } = Request({
    urlApi: hasProcedureId(procedureId)
      ? `/Procedure/${procedureId}/sub-procedures`
      : null,
  });

  useEffect(() => {
    let isMounted = true;

    async function fetchParentData() {
      if (!hasProcedureId(procedureId)) {
        setParentValuesLoaded(true);
        return;
      }

      try {
        const valuesRes = await requestAuth(
          "get",
          `/Procedure/${procedureId}/dataValues?${buildFieldNamesQuery()}`,
        );
        console.log("DATA VALUES PADRE:", valuesRes.data);
        if (isMounted) setParentValues(valuesRes.data || {});
      } catch (err) {
        console.error("Error fetching parent data values", err);
      } finally {
        if (isMounted) setParentValuesLoaded(true);
      }
    }

    fetchParentData();
    return () => {
      isMounted = false;
    };
  }, [procedureId]);

  useEffect(() => {
    let isMounted = true;

    async function fetchParentBitacora() {
      if (!hasProcedureId(procedureId)) {
        setParentBitacoraLoaded(true);
        return;
      }

      try {
        const res1 = await requestAuth(
          "get",
          `/Procedure/${procedureId}/datasets?fieldnames=historialDeSeguimiento`,
        );
        const fieldId = res1.data?.find(
          (field) => field.name === "historialDeSeguimiento",
        )?.id;
        if (fieldId && isMounted) {
          const res2 = await requestAuth(
            "get",
            `/dataSet/procedure/${procedureId}/field/${fieldId}/subdata`,
          );
          if (isMounted) setParentBitacora(res2.data || []);
        }
      } catch (err) {
        console.error("Error fetching parent bitacora", err);
      } finally {
        if (isMounted) setParentBitacoraLoaded(true);
      }
    }

    fetchParentBitacora();
    return () => {
      isMounted = false;
    };
  }, [procedureId]);

  useEffect(() => {
    if (
      !subProcedures ||
      !Array.isArray(subProcedures) ||
      subProcedures.length === 0
    )
      return;
    let isMounted = true;

    async function fetchAllSubValues() {
      const results = await Promise.all(
        subProcedures.map(async (sub) => {
          const subId = resolveProcedureId(sub);
          if (!hasProcedureId(subId)) return null;

          try {
            const res = await requestAuth(
              "get",
              `/Procedure/${subId}/dataValues?${buildFieldNamesQuery()}`,
            );
            return { subId, values: res.data || {} };
          } catch {
            return { subId, values: {} };
          }
        }),
      );

      if (!isMounted) return;
      const map = {};
      results.filter(Boolean).forEach((item) => {
        map[item.subId] = item.values;
      });
      setSubValuesMap(map);
    }

    fetchAllSubValues();
    return () => {
      isMounted = false;
    };
  }, [subProcedures]);

  const parentRow = {
    ...safeProcedure,
    ...parentValues,
    id: procedureId,
    trackingKind: "parent",
    trackingLabel: "Trámite principal",
  };

  const validSubProcedures = Array.isArray(subProcedures)
    ? subProcedures.filter((subProc) =>
        hasProcedureId(resolveProcedureId(subProc)),
      )
    : [];
  const hasSubProcedures = validSubProcedures.length > 0;
  const trackingRows = hasSubProcedures
    ? [
        parentRow,
        ...validSubProcedures.map((subProc, index) => {
          const subId = resolveProcedureId(subProc);
          return {
            ...mergeWithFallback(parentRow, {
              ...subProc,
              ...(subValuesMap[subId] || {}),
            }),
            id: subId,
            trackingKind: "sub",
            trackingLabel: `Subtrámite ${index + 1}`,
          };
        }),
      ]
    : [parentRow];

  const isLoading = !subProceduresLoaded || !parentValuesLoaded;
  const firstTrackingId = trackingRows[0]?.id;
  const currentRow =
    trackingRows.find((row) => String(row.id) === String(activeTracking)) ??
    trackingRows[0];
  const isExpanded = expandedBitacoraId === currentRow.id;

  useEffect(() => {
    if (activeTracking === null && hasProcedureId(firstTrackingId)) {
      setActiveTracking(firstTrackingId);
    }
  }, [activeTracking, firstTrackingId]);

  if (!hasProcedureId(procedureId)) {
    return (
      <div className="w-[96vw] max-w-[720px] bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl relative">
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between gap-4">
          <h3 className="text-start m-0 text-base font-semibold text-slate-900">
            Seguimiento de importación
          </h3>
          <button
            type="button"
            onClick={dialogClose}
            className="text-slate-400 hover:text-red-600 text-lg font-bold w-9 h-9 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
          >
            X
          </button>
        </div>
        <div className="p-6 text-sm text-slate-500">
          No se pudo cargar el seguimiento porque el trámite no tiene un ID
          válido.
        </div>
      </div>
    );
  }

  return (
    <div className="w-[96vw] max-w-[1180px] h-[88vh] bg-slate-50 rounded-2xl overflow-hidden flex flex-col shadow-2xl relative">
      <div className="shrink-0 bg-white border-b border-slate-200 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-500">
              Seguimiento de importación
            </div>
            <h3 className="text-start m-0 mt-1 text-base font-semibold text-slate-900">
              {parentRow.codigoDeInterno ||
                safeProcedure.codigoDeInterno ||
                `Trámite ${procedureId}`}
            </h3>
            <div className="mt-1 text-sm text-slate-500">
              {trackingRows.length}{" "}
              {trackingRows.length === 1 ? "embarque" : "embarques"} entre
              trámite y subtrámites
            </div>
          </div>
          <button
            type="button"
            onClick={dialogClose}
            className="text-slate-400 hover:text-red-600 text-2xl font-bold w-9 h-9 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
          >
            X
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {trackingRows.map((row) => {
            const active = String(activeTracking) === String(row.id);
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => setActiveTracking(row.id)}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${
                  active
                    ? "border-orange-400 bg-orange-50 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`font-semibold ${active ? "text-orange-700" : "text-slate-700"}`}
                  >
                    {row.trackingLabel}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        <button
          type="button"
          onClick={() =>
            setExpandedBitacoraId(isExpanded ? null : currentRow.id)
          }
          className="self-start lg:self-center shrink-0 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-colors"
        >
          {isExpanded ? "Ocultar bitácora" : "Ver bitácora"}
        </button>
        {isLoading ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-500">
            <span className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-orange-200 border-t-orange-500"></span>
            <div className="mt-3 text-sm">Cargando seguimiento...</div>
          </div>
        ) : trackingRows.length === 0 ? (
          <div className="text-center text-slate-500 mt-10">
            No hay datos para mostrar
          </div>
        ) : (
          <div className="p-4 space-y-5">
            {Object.entries(SECTIONS).map(([key, section]) => (
              <section
                key={key}
                className="border border-slate-200 rounded-2xl overflow-hidden bg-white"
              >
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-sm">
                    {section.marker}
                  </span>

                  <div>
                    <h4 className="font-bold text-slate-900">
                      {section.label}
                    </h4>

                    <p className="text-xs text-slate-500">{section.helper}</p>
                  </div>
                </div>

                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {(() => {
                    const fields =
                      key === "transito"
                        ? getTransitFields(currentRow)
                        : section.fields;

                    return fields.map((field) => {
                      const value = formatValue(currentRow, field);

                      return (
                        <div
                          key={`${currentRow.id}-${field.name}`}
                          className={`rounded-xl border p-3 ${
                            field.emphasis
                              ? "border-orange-200 bg-orange-50/50"
                              : "border-slate-100 bg-slate-50/70"
                          }`}
                        >
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                            {field.label}
                          </div>

                          <div className="mt-1 text-sm break-words text-slate-700">
                            {value}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {key === "transito" && getContainerGroups(currentRow).length > 0 && (
                  <div className="px-4 pb-4">
                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                        Detalle de contenedores
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-white text-[11px] uppercase text-slate-400">
                            <tr>
                              <th className="px-3 py-2 text-left">Cantidad</th>
                              <th className="px-3 py-2 text-left">Sigla</th>
                              <th className="px-3 py-2 text-left">Bill of Lading</th>
                              <th className="px-3 py-2 text-left">Precinto</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {getContainerGroups(currentRow).map((group, groupIndex) => (
                              <tr key={groupIndex}>
                                <td className="px-3 py-2 font-semibold text-slate-700">
                                  {group.quantityLabel || `${group.quantity} X ${group.containerType}`}
                                </td>
                                <td className="px-3 py-2 font-mono text-slate-700">
                                  <div className="flex flex-col gap-1">
                                    {group.containers.map((container, containerIndex) => (
                                      <span key={containerIndex}>{container.code || "-"}</span>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {group.billOfLading || "-"}
                                </td>
                                <td className="px-3 py-2 font-mono text-slate-700">
                                  <div className="flex flex-col gap-1">
                                    {group.containers.map((container, containerIndex) => (
                                      <span key={containerIndex}>{container.seal || "-"}</span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            ))}

            {isExpanded && (
              <section className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h4 className="font-bold text-slate-900">Bitácora</h4>
                </div>

                <div className="p-4">
                  <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                    <ExpandedBitacora
                      procedureId={currentRow.id}
                      fallbackData={parentBitacora}
                      fallbackLoading={!parentBitacoraLoaded}
                    />
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
